import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { PartyPicker, type PartyRef } from '@/components/parties/PartyPicker';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';

interface LinkEmailCustomerDialogProps {
  open: boolean;
  conversationId: string;
  senderAddress: string | null;
  onClose: () => void;
  onLinked: (party: PartyRef) => void;
}

/** Link a workshop mailbox thread to a person or company by hand (#3736). */
export function LinkEmailCustomerDialog({
  open,
  conversationId,
  senderAddress,
  onClose,
  onLinked,
}: LinkEmailCustomerDialogProps) {
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const [saving, setSaving] = useState(false);

  const link = async (party: PartyRef) => {
    const partyId = party.personId ?? party.companyId;
    if (!partyId) return;
    setSaving(true);
    try {
      await api.patch(`/conversations/${conversationId}/participant`, { partyId });
      notify.success(t('emailLinked'), party.name);
      onLinked(party);
    } catch {
      notify.error(t('emailLinkFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={saving ? undefined : onClose}
      mobileTitle={t('emailLinkTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>{t('emailLinkTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {senderAddress ? t('emailLinkDescription', { address: senderAddress }) : t('emailLinkDescriptionNoAddress')}
          </Typography>
          <PartyPicker onSelect={(party) => { void link(party); }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {tc('cancel')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
